/* Quick-view modal controller for upsell cards. One instance handles every .mp-qv on the page. */
import { utils } from "./-global.min.js";

(function () {
  "use strict";

  function parseData(modal) {
    var el = modal.querySelector(".mp-qv__data");
    if (!el) return { variants: [] };
    try { return JSON.parse(el.textContent); } catch (e) { return { variants: [] }; }
  }

  // Images grouped by colour (matched by media alt text, like the PDP gallery).
  function parseImages(modal) {
    var el = modal.querySelector(".mp-qv__images");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Rebuild the side thumbnails to the images associated with the selected colour.
  function setColorThumbs(modal, color) {
    var imgs = parseImages(modal);
    if (!imgs || !color) return;
    var list = imgs[color];
    if (!list || !list.length) return;
    var thumbs = modal.querySelector(".mp-qv__thumbs");
    if (!thumbs) return;
    var main = modal.querySelector(".mp-qv__main-img");
    var currentMain = main ? main.getAttribute("src") : "";
    thumbs.innerHTML = list.slice(0, 3).map(function (url) {
      var active = url === currentMain ? " is-active" : "";
      return '<button type="button" class="mp-qv__thumb' + active + '" data-qv-thumb="' + url +
        '"><img src="' + url + '" alt="" loading="lazy"></button>';
    }).join("");
    if (!thumbs.querySelector(".is-active")) {
      var first = thumbs.querySelector(".mp-qv__thumb");
      if (first) first.classList.add("is-active");
    }
  }

  // The currently-selected value for each option index, read from the active buttons.
  function selectedOptions(modal) {
    var sel = [];
    modal.querySelectorAll("[data-qv-optgroup]").forEach(function (group) {
      var idx = parseInt(group.getAttribute("data-qv-optgroup"), 10);
      var active = group.querySelector(".is-active");
      sel[idx] = active ? active.getAttribute("data-qv-val") : null;
    });
    return sel;
  }

  function matchVariant(variants, sel) {
    return variants.find(function (v) {
      return v.options.every(function (o, i) { return sel[i] == null || o === sel[i]; });
    });
  }

  // Cross out sizes that have no available variant for the currently-selected colour.
  function updateSizeAvailability(modal) {
    var data = parseData(modal);
    var sel = selectedOptions(modal);
    modal.querySelectorAll(".mp-qv__sizes").forEach(function (group) {
      var sizeIdx = parseInt(group.getAttribute("data-qv-optgroup"), 10);
      group.querySelectorAll(".mp-qv__size").forEach(function (btn) {
        var val = btn.getAttribute("data-qv-val");
        var ok = data.variants.some(function (v) {
          if (v.options[sizeIdx] !== val) return false;
          for (var i = 0; i < sel.length; i++) {
            if (i === sizeIdx) continue;            // ignore this size's own axis
            if (sel[i] != null && v.options[i] !== sel[i]) return false; // must match colour, etc.
          }
          return v.available;
        });
        btn.classList.toggle("is-unavailable", !ok);
        btn.disabled = !ok;
      });
    });
  }

  function syncVariant(modal) {
    var data = parseData(modal);
    var sel = selectedOptions(modal);
    var variant = matchVariant(data.variants, sel);
    var atc = modal.querySelector("[data-qv-add]");
    var label = modal.querySelector(".mp-qv__atc-label");
    var price = modal.querySelector(".mp-qv__atc-price");
    if (!atc) return;

    if (variant) {
      atc.setAttribute("data-variant-id", variant.id);
      if (price) price.innerHTML = variant.priceHtml || variant.price;
      if (variant.available) {
        atc.disabled = false;
        if (label) label.textContent = "Add to Cart";
      } else {
        atc.disabled = true;
        if (label) label.textContent = "Sold Out";
      }
      // Swap the main image to the variant's image when it has one.
      if (variant.image) {
        var mainImg = modal.querySelector(".mp-qv__main-img");
        if (mainImg) mainImg.src = variant.image;
      }
    } else {
      atc.disabled = true;
      if (label) label.textContent = "Unavailable";
    }

    // Cross out sizes that aren't available in the current colour.
    updateSizeAvailability(modal);
  }

  function openModal(modal) {
    // Move to <body> so a transformed ancestor (sticky info / animations) can't
    // trap the position:fixed overlay inside its own containing block.
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    modal.classList.add("is-open");
    document.documentElement.classList.add("mp-qv-lock");

    // Paint the full-screen dark backdrop directly on the element. Inline styles
    // can't be defeated by stylesheet caching, load order, specificity, or `inset`
    // support — if the modal opens, the screen darkens.
    var s = modal.style;
    s.position = "fixed";
    s.top = "0"; s.right = "0"; s.bottom = "0"; s.left = "0";
    s.zIndex = "99999";
    s.display = "flex";
    s.alignItems = "center";
    s.justifyContent = "center";
    s.padding = "20px";
    s.background = "rgba(45, 38, 27, 0.2)";

    syncVariant(modal);
    // Show the thumbnails for the colour that's selected on open
    var activeSwatch = modal.querySelector(".mp-qv__swatch.is-active");
    if (activeSwatch) setColorThumbs(modal, activeSwatch.getAttribute("data-qv-val"));
  }

  function closeModal(modal) {
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("is-open");
    // Inline display:flex would otherwise beat the [hidden] rule and keep it visible.
    modal.style.display = "none";
    if (!document.querySelector(".mp-qv.is-open")) {
      document.documentElement.classList.remove("mp-qv-lock");
    }
  }

  function addToCart(modal, atc) {
    var id = atc.getAttribute("data-variant-id");
    if (!id || atc.disabled) return;
    var label = modal.querySelector(".mp-qv__atc-label");
    var orig = label ? label.textContent : "";
    atc.disabled = true;
    if (label) label.textContent = "Adding…";

    // Use the theme's cart pipeline so the mini-cart actually updates + opens
    // (a plain /cart/add.js fetch adds the item but never refreshes the UI).
    utils
      .addToCart({ items: [{ id: Number(id), quantity: 1 }] })
      .then(function (res) {
        if (res && res.sections) utils.reRenderShopifySections(res);
        closeModal(modal);
        if (!document.body.classList.contains("mini-cart-is-active")) {
          utils.toggle(document.body, ["mini-cart-is-active"]);
        }
      })
      .catch(function () {})
      .finally(function () {
        if (label) label.textContent = orig;
        atc.disabled = false;
      });
  }

  document.addEventListener("click", function (e) {
    // Open trigger
    var opener = e.target.closest("[data-qv-open]");
    if (opener) {
      e.preventDefault();
      var modal = document.getElementById(opener.getAttribute("data-qv-open"));
      if (modal) openModal(modal);
      return;
    }

    var inModal = e.target.closest(".mp-qv");
    if (!inModal) return;

    // Close on the ✕, the overlay, or any click inside the modal but outside the dialog box.
    if (e.target.closest("[data-qv-close]") || !e.target.closest(".mp-qv__dialog")) {
      closeModal(inModal);
      return;
    }

    // Thumbnail swap
    var thumb = e.target.closest("[data-qv-thumb]");
    if (thumb) {
      var main = inModal.querySelector(".mp-qv__main-img");
      if (main) main.src = thumb.getAttribute("data-qv-thumb");
      inModal.querySelectorAll(".mp-qv__thumb").forEach(function (t) { t.classList.remove("is-active"); });
      thumb.classList.add("is-active");
      return;
    }

    // Option (swatch / size) selection
    var optBtn = e.target.closest("[data-qv-opt]");
    if (optBtn) {
      if (optBtn.disabled) return; // crossed-out (unavailable) size
      var group = optBtn.closest("[data-qv-optgroup]");
      if (group) group.querySelectorAll("[data-qv-opt]").forEach(function (b) { b.classList.remove("is-active"); });
      optBtn.classList.add("is-active");
      syncVariant(inModal);
      // Colour swatch → swap the side thumbnails to that colour's images
      if (optBtn.classList.contains("mp-qv__swatch")) {
        setColorThumbs(inModal, optBtn.getAttribute("data-qv-val"));
      }
      return;
    }

    // Add to cart
    var add = e.target.closest("[data-qv-add]");
    if (add) { addToCart(inModal, add); return; }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var open = document.querySelector(".mp-qv.is-open");
    if (open) closeModal(open);
  });
})();

/* On desktop, move the Product Stats block into the gallery column so it sits in the
   empty space below the gallery (the hero's overlapping grid leaves columns 1-7 open
   under the sticky gallery). Reversible: it returns to its original spot on mobile. */
(function () {
  var stats = document.querySelector(".product-hero .mp-stats");
  var carousel = document.querySelector(".product-hero .product-hero__carousel");
  if (!stats || !carousel) return;

  // Remember the original location so we can restore it on mobile.
  var home = document.createComment("mp-stats-home");
  stats.parentNode.insertBefore(home, stats);

  function galleryWidth() {
    var g = carousel.querySelector(".slick-list, .slick-slider, .carousel__wrapper");
    return g ? Math.round(g.getBoundingClientRect().width) : 0;
  }

  function place() {
    // 1024px = the breakpoint where the desktop two-column gallery (with the stats
    // sitting under the gallery column) applies. Below it the gallery is stacked, so
    // keep the stats in their normal info-column position. The full-width desktop
    // layout (data-pdp-full-width) keeps the two columns down to 768px.
    var fullWidth = carousel.closest("[data-pdp-full-width]");
    var desktop = window.matchMedia(fullWidth ? "(min-width: 768px)" : "(min-width: 1024px)").matches;
    if (desktop) {
      if (stats.parentNode !== carousel) carousel.appendChild(stats);
      stats.classList.add("mp-stats--under-gallery");
      var w = galleryWidth();
      stats.style.maxWidth = w ? w + "px" : "";
    } else {
      stats.classList.remove("mp-stats--under-gallery");
      stats.style.maxWidth = "";
      if (home.parentNode && stats.parentNode !== home.parentNode) {
        home.parentNode.insertBefore(stats, home);
      }
    }
  }

  place();
  var t;
  window.addEventListener("resize", function () {
    clearTimeout(t);
    t = setTimeout(place, 150);
  });
})();

